import { useAuthActions } from "@frontegg/react";
import { LogOut } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { usePreviewSession } from "../../../../contexts/PreviewSessionContext";

interface DrawerFooterProps {
  isOpen: boolean;
}

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  show: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, show }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHovered && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
  }, [isHovered]);

  return (
    <div 
      ref={ref}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {show && isHovered && (
        <div 
          className="fixed z-[9999] pointer-events-none"
          style={{ 
            top: position.top,
            left: position.left,
            transform: 'translateY(-50%)'
          }}
        >
          <div className="bg-[#1a2744] text-[#DDEBFF] text-[13px] font-[300] px-3 py-1.5 rounded-md whitespace-nowrap shadow-lg border border-[#2a3a5c]">
            {text}
          </div>
        </div>
      )}
    </div>
  );
};

export default function DrawerFooter(props: DrawerFooterProps) {
  const previewCtx = usePreviewSession();
  const inPreview = !!previewCtx;
  // Frontegg's useAuthActions requires its provider; in preview mode there is
  // no FronteggProvider, so call it conditionally. The branch is stable for
  // the lifetime of the App instance (decided in main.tsx), satisfying React's
  // rules-of-hooks invariant.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const actions = inPreview ? null : useAuthActions();

  const handleLogout = () => {
    if (inPreview) {
      window.location.href = "/preview-ended";
      return;
    }
    actions?.logout?.();
  };

  return (
    <div className="mt-[10px]">
      <div className={`logout-btn ${props?.isOpen ? "mb-[30px]" : ""} `}>
        <p
          className={`line w-full ${
            props?.isOpen ? "" : "hidden"
          } h-[5px] rounded-full border-[#1e264569] border-solid border-[1px]`}
        ></p>
        <Tooltip text="Logout" show={!props.isOpen}>
          <button
            onClick={handleLogout}
            className={`flex h-[36px] transition-all duration-300 group items-center text-[#DDEBFF] hover:bg-[#0193F8] hover:font-[400] hover:text-[#05050F] ${
              props?.isOpen
                ? "my-[0.8rem] rounded-[12px]"
                : "flex items-center justify-center my-0 h-[50px] rounded-[10px]"
            } px-4 text-white w-full text-[15px]`}
          >
            <LogOut
              className={`${
                props?.isOpen ? "mr-3" : "text-[#DDEBFF]"
              } group-hover:text-[#042849]`}
              size={18}
            />{" "}
            {props?.isOpen ? <span>Logout</span> : ""}
          </button>
        </Tooltip>
        <p
          className={`line w-full h-[5px] ${
            props?.isOpen ? "" : "hidden"
          } rounded-full border-[#1e264569] border-solid border-[1px]`}
        ></p>
      </div>
      <div
        className={`text-[#6B96D680] text-[12px] leading-[16px] font-[200] text-center ${
          props?.isOpen ? "" : "hidden"
        }`}
      >
        <p className="whitespace-nowrap">© 2026 FixFinder Inc.</p>
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#6B96D680] hover:text-[#0193F8] transition-colors duration-200 underline"
        >
          Terms Of Use
        </a>
      </div>
    </div>
  );
}
